import type { Species } from '../../types';
import { speciesList } from '../region';

// Binomials for birds that do not exist.
//
// Every wrong answer in a Name Match question used to be a real bird from the
// region, which leaves a shortcut: a learner who has seen all the region's
// binomials go past can pick the one that "belongs with" the common name by
// elimination, or by recognising the other three as names already attached to
// other birds. A made-up name closes that. "Turdus pluvialis" looks as much like
// the robin as "Turdus migratorius" does to anyone who only knows the genus, so
// the question can only be answered by knowing the epithet too.
//
// Two kinds. Most are an invented epithet in a genus the region really has —
// the strongest decoy, since the genus is right and only the second word gives
// it away — declined to agree with the genus where its gender is settled, and
// otherwise in the -is/-ens/-ans forms that agree with any. The rest are wholly
// invented genera: Latin- and Greek-built, and a few of them jokes that a
// learner who reads Latin will enjoy catching ("Empidonax indistinctus").
//
// None may be a real species. The test suite checks every one against the
// installed packs; beyond those, each epithet was chosen to be absent from its
// genus worldwide, not only locally — a decoy that turns out to be a real bird
// elsewhere would be a wrong answer marked wrong for the wrong reason.

/** Invented epithets, keyed by a real genus from one of the region packs. */
const IN_REAL_GENERA: Record<string, readonly string[]> = {
  // Waterfowl
  Anser: ['borealis', 'irascibilis', 'griseonotus'],
  Branta: ['belligerens', 'pallidicollis'],
  Cygnus: ['tacitus', 'glacialis'],
  Aix: ['ornatissima'],
  Spatula: ['aurantipes', 'pallescens'],
  Mareca: ['rufigena', 'tenuirostris'],
  Anas: ['vespertina', 'lacustris', 'pallidiventris'],
  Aythya: ['cinereiceps', 'obscurata'],
  Histrionicus: ['pictus'],
  Melanitta: ['rostrata', 'albifrons'],
  Clangula: ['caudata'],
  Bucephala: ['nivea', 'orientalis'],
  Lophodytes: ['cristatellus'],
  Mergus: ['fluviatilis', 'cinerascens'],
  Oxyura: ['caerulirostris'],
  Alopochen: ['rufescens'],
  // Gamebirds
  Callipepla: ['elegantula', 'montana'],
  Meleagris: ['cristata'],
  Bonasa: ['pinicola'],
  Dendragapus: ['montivagus'],
  Phasianus: ['aureicollis'],
  Numida: ['punctata'],
  Pternistis: ['rupicola'],
  // Grebes, pigeons, nightjars, swifts, hummingbirds
  Podilymbus: ['lacustris'],
  Podiceps: ['rufipectus', 'cascadensis'],
  Aechmophorus: ['borealis'],
  Zenaida: ['lugubrissima', 'pallescens'],
  Patagioenas: ['cinerascens'],
  Streptopelia: ['monotona', 'pallidipennis'],
  Spilopelia: ['murina'],
  Columba: ['urbanissima', 'cinerifrons'],
  Chordeiles: ['vespertinus'],
  Chaetura: ['nebulosa'],
  Archilochus: ['violaceus'],
  Calypte: ['purpurata'],
  Selasphorus: ['aurantius'],
  // Cranes, rails, shorebirds
  Antigone: ['cinerascens'],
  Rallus: ['occultus'],
  Porzana: ['umbrosa'],
  Fulica: ['nitens'],
  Himantopus: ['gracilipes'],
  Haematopus: ['rubripes'],
  Pluvialis: ['argentea'],
  Charadrius: ['lacustris', 'pallidifrons'],
  Vanellus: ['clamosus'],
  Numenius: ['falcatus'],
  Limosa: ['cinnamomea'],
  Arenaria: ['versicolor'],
  Calidris: ['pallidula', 'cinnamomea'],
  Limnodromus: ['maculatus'],
  Gallinago: ['silvicola'],
  Actitis: ['punctatus'],
  Tringa: ['rufipes', 'aurantipes'],
  // Alcids, gulls, terns
  Uria: ['nigrescens'],
  Cepphus: ['maculatus'],
  Brachyramphus: ['fuscus'],
  Cerorhinca: ['bicornis'],
  Hydroprogne: ['orientalis'],
  Chroicocephalus: ['pallidirostris'],
  Larus: ['chipsivorus', 'impudens', 'griseonotus'],
  // Loons, cormorants, pelicans, herons, ibises
  Gavia: ['maculata'],
  Urile: ['nitens'],
  Nannopterum: ['cristatum'],
  Microcarbo: ['minimus'],
  Phalacrocorax: ['nitidus'],
  Pelecanus: ['gularis'],
  Botaurus: ['umbratilis'],
  Butorides: ['fuscicollis'],
  Ardea: ['glaucescens', 'nebulosa'],
  Threskiornis: ['sacerrimus'],
  Bostrychia: ['clamans'],
  // Raptors and owls
  Cathartes: ['fuscicollis'],
  Pandion: ['fluvialis'],
  Aquila: ['cinerascens'],
  Accipiter: ['velocissimus', 'fumosus'],
  Circus: ['pallidiceps'],
  Haliaeetus: ['nobilis'],
  Buteo: ['fulvicauda', 'ventosus'],
  Tyto: ['argentea'],
  Bubo: ['insomnis', 'cascadensis'],
  Strix: ['umbratica', 'taciturna'],
  Asio: ['crepuscularis'],
  // Kingfishers, woodpeckers, falcons
  Megaceryle: ['caerulescens'],
  Melanerpes: ['roseiventris'],
  Sphyrapicus: ['aureus'],
  Dryobates: ['montanus'],
  Leuconotopicus: ['nebulosus'],
  Colaptes: ['cinnamomeus'],
  Dryocopus: ['rubricapillus'],
  Falco: ['fulminans', 'nebularis'],
  // Flycatchers, vireos, shrikes, corvids
  Tyrannus: ['iracundus'],
  Contopus: ['tristis'],
  Empidonax: ['indistinctus', 'dubius'],
  Sayornis: ['pallescens'],
  Vireo: ['loquacissimus', 'umbrosus'],
  Lanius: ['carnifex'],
  Perisoreus: ['furtivus'],
  Cyanocitta: ['impertinens'],
  Aphelocoma: ['cinerea'],
  Nucifraga: ['immemor'],
  Pica: ['furax'],
  Corvus: ['sapientissimus', 'nocturnus'],
  // Small songbirds
  Poecile: ['nivalis', 'nigrifrons'],
  Eremophila: ['deserticola'],
  Riparia: ['fluvialis'],
  Tachycineta: ['aurata'],
  Stelgidopteryx: ['griseiventris'],
  Progne: ['purpurascens'],
  Hirundo: ['celerrima'],
  Petrochelidon: ['lutescens'],
  Ptyonoprogne: ['umbrina'],
  Psaltriparus: ['gregarius'],
  Corthylio: ['flammiceps'],
  Regulus: ['minutissimus'],
  Bombycilla: ['sericea'],
  Sitta: ['inversa'],
  Certhia: ['spiralis'],
  Salpinctes: ['rupestris'],
  Thryomanes: ['vociferans'],
  Troglodytes: ['vociferissimus', 'muscicola'],
  Cistothorus: ['arundinaceus'],
  Dumetella: ['felina'],
  Sturnus: ['obstreperus'],
  Onychognathus: ['rubripennis'],
  Cinclus: ['submersus'],
  Sialia: ['azurea'],
  Myadestes: ['montivagus'],
  Catharus: ['crepuscularis', 'umbrinus'],
  Turdus: ['pluvialis', 'vermivorus'],
  Ixoreus: ['aurantius'],
  Sigelus: ['garrulus'],
  Dessonornis: ['montanus'],
  Prinia: ['garrula'],
  Pycnonotus: ['hilaris'],
  Zosterops: ['loquax'],
  Nectarinia: ['smaragdina'],
  Cinnyris: ['opalinus'],
  Ploceus: ['architectus'],
  Passer: ['impudens'],
  Motacilla: ['oscillans'],
  Anthus: ['pallidiventris'],
  // Finches, sparrows, blackbirds, warblers, tanagers
  Hesperiphona: ['aurantiaca'],
  Haemorhous: ['roseiceps'],
  Loxia: ['obliqua'],
  Spinus: ['flavissimus'],
  Serinus: ['melodiosus'],
  Spizella: ['rufescens'],
  Passerella: ['montana'],
  Junco: ['nivalis'],
  Zonotrichia: ['aurifrons'],
  Pooecetes: ['campestris'],
  Passerculus: ['maritimus'],
  Melospiza: ['cantatrix'],
  Pipilo: ['foliicola'],
  Icteria: ['garrula'],
  Xanthocephalus: ['palustris'],
  Sturnella: ['aurea'],
  Icterus: ['flammeus'],
  Agelaius: ['rubricatus'],
  Molothrus: ['parasiticus'],
  Euphagus: ['nitidus'],
  Leiothlypis: ['pallida'],
  Geothlypis: ['larvata'],
  Setophaga: ['aurantiaca', 'ignota'],
  Cardellina: ['aurifrons'],
  Piranga: ['ignea'],
  Pheucticus: ['castaneiceps'],
  Passerina: ['azurescens'],
};

/** Wholly invented binomials — the genus is as fictional as the bird. */
const INVENTED: readonly string[] = [
  'Aurivox cantans',
  'Nebulornis griseus',
  'Crepusculia vespertina',
  'Tenebricola noctivaga',
  'Paludornis stagnalis',
  'Glaucopsar pallidus',
  'Silvanella umbrosa',
  'Saxicantor rupestris',
  'Ochrothraupis flavescens',
  'Melanothlypis obscura',
  'Cyanothlypis azurina',
  'Rhodospizella rosea',
  'Chionornis borealis',
  'Hyloxenus arboricola',
  'Oreocantor montivagus',
  'Limnopsar palustris',
  'Psammornis arenicola',
  'Dendrocantor sylvaticus',
  'Ptilomixis variegata',
  'Stenopterella gracilis',
  'Brachyornis brevicauda',
  'Callistornis splendens',
  'Nycticantor lunaris',
  'Umbrornis fuliginosa',
  'Erythrosylvia rubescens',
  'Chalcopsar aeneus',
  'Argyrornis nitens',
  'Cinerella grisea',
  'Ammocantor deserticola',
  'Boreospiza arctica',
  'Austrolanius meridionalis',
  'Zephyrornis levis',
  'Vesperopsar crepuscularis',
  'Pluviornis madidus',
  'Frigidornis glacialis',
  'Cryptoparus occultus',
  'Microspiza minima',
  'Megalospiza grandis',
  'Rhamphocantor longirostris',
  'Ochropsar fulvus',
  'Amethystornis violacea',
  'Smaragdella viridis',
  'Pinicantor coniferus',
  'Quercornis glandivorus',
  'Myrmecocantor formicarius',
  'Calamocantor arundinis',
  'Nivicola hiemalis',
  'Halocantor maritimus',
  'Litoricola sabulosa',
  'Hydrospiza rivalis',
  'Torrentornis impetuosa',
  'Speleornis tenebrosa',
  'Petrocantor lapidarius',
  'Alticantor cacuminis',
  'Collispiza collina',
  'Pratospiza campestris',
  'Aerovolans celerrimus',
  'Velocornis rapidus',
  'Pseudocorvus mimeticus',
  'Parasturnus imitator',
  'Tympanornis percussor',
  'Fistulornis tibicen',
  'Sibilornis susurrans',
  'Strepitornis obstreperus',
  'Crepitornis crepitans',
  'Plumbospiza cinerea',
  'Lanugornis mollis',
  'Lophocantor cristatus',
  'Barbatornis mystacinus',
  'Porphyropsar purpureus',
  'Leucocantor niveus',
  'Anthovora nectarina',
  'Nemoricantor silvestris',
  'Ripariella fluvialis',
  'Rupispiza saxicola',
  'Ericornis callunae',
  'Stipicola graminea',
  // The jokes. Each still has to pass as a name on a quiz card, so they are
  // real Latin, correctly formed, and only funny once you read them.
  'Tardivola procrastinans',
  'Somnolentia perpetua',
  'Dubiornis ambiguus',
  'Mendacornis fallax',
  'Loquaxilla interminabilis',
  'Pseudopasser imaginarius',
  'Nullavis inanis',
  'Obscurella ignota',
  'Vagabundus errans',
  'Confusornis perplexus',
  'Vanitornis gloriosus',
  'Musciphaga voracissima',
  'Baccivora frugalis',
  'Lentivolans pigra',
  'Echovox reverberans',
  'Silentornis tacita',
  // From Georges Perec's mock paper on the "yelling reaction" of the soprano:
  // a species that has only ever existed in a spoof of a journal. It earns its
  // place.
  'Cantatrix sopranica',
];

/** Every fake binomial, real-genus ones first. */
export const FAKE_BINOMIALS: readonly string[] = [
  ...Object.entries(IN_REAL_GENERA).flatMap(([genus, epithets]) =>
    epithets.map((e) => `${genus} ${e}`),
  ),
  ...INVENTED,
];

/**
 * The prefix that marks a choice as a made-up name rather than a species code.
 *
 * Choices are species codes everywhere else in the quiz, and a fake one has to
 * travel in the same list — shuffled, marked, re-asked — without ever being
 * mistaken for a bird. A colon cannot appear in an eBird code, so no real code
 * can collide with one of these.
 */
const PREFIX = 'fake:';

/** Whether a choice is a made-up binomial rather than a species code. */
export function isFakeName(choice: string): boolean {
  return choice.startsWith(PREFIX);
}

/** The binomial a fake choice stands for. */
export function fakeBinomial(choice: string): string {
  return choice.slice(PREFIX.length);
}

function genusOf(binomial: string): string {
  return binomial.split(' ')[0];
}

function pickOne<T>(list: readonly T[], rnd: () => number): T | undefined {
  return list.length ? list[Math.floor(rnd() * list.length)] : undefined;
}

/**
 * `count` fake choices for a question about `target`, never one whose name is
 * in `avoid` (the binomials already on the card).
 *
 * Each pick leans towards the most convincing decoy the pool has: half the time
 * a fake in the target's own genus, a fifth of the time one from another genus
 * in its family, and otherwise one from a genus in its order or a wholly
 * invented one. Never a real genus from elsewhere in the tree — "Circus
 * pallidiceps" under a chickadee is a harrier, and a learner who knows only
 * that much discards it for free. Any tier with nothing left in it falls
 * through to the next.
 */
export function pickFakeNames(
  target: Species,
  count: number,
  rnd: () => number,
  avoid: ReadonlySet<string> = new Set(),
): string[] {
  const genus = genusOf(target.sci);
  const familyGenera = new Set<string>();
  const orderGenera = new Set<string>();
  const knownGenera = new Set<string>();
  for (const sp of speciesList()) {
    const g = genusOf(sp.sci);
    knownGenera.add(g);
    if (target.family && sp.family === target.family) familyGenera.add(g);
    if (target.order && sp.order === target.order) orderGenera.add(g);
  }
  // A real genus none of the installed packs holds tells the learner nothing
  // either way, so it counts with the invented ones.
  const neutral = (g: string): boolean => orderGenera.has(g) || !knownGenera.has(g);

  const taken = new Set([...avoid, target.sci]);
  const out: string[] = [];
  const open = (name: string): boolean => !taken.has(name);

  for (let i = 0; i < count; i++) {
    const sameGenus = FAKE_BINOMIALS.filter((n) => genusOf(n) === genus && open(n));
    const sameFamily = FAKE_BINOMIALS.filter(
      (n) => familyGenera.has(genusOf(n)) && genusOf(n) !== genus && open(n),
    );
    const wider = FAKE_BINOMIALS.filter((n) => neutral(genusOf(n)) && open(n));
    const anything = FAKE_BINOMIALS.filter(open);
    const roll = rnd();
    const tiers =
      roll < 0.5
        ? [sameGenus, sameFamily, wider, anything]
        : roll < 0.7
          ? [sameFamily, wider, anything]
          : [wider, anything];
    const tier = tiers.find((t) => t.length) ?? [];
    const name = pickOne(tier, rnd);
    if (!name) break;
    taken.add(name);
    out.push(PREFIX + name);
  }
  return out;
}
